import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Router, ActivatedRoute } from '@angular/router';
import { DeviceDetectorService } from 'ngx-device-detector';

import { Subject } from 'rxjs';
import { take } from 'rxjs/operators';

/** @internal */
interface OSInfo {
    syst: string | null;   // win / linux / mac
    proc: string | null;   // 32 / 64
    arch: string | null;   // ARM / intel
    fext: string | null;   // executable files extension
}

/** @internal */
type AvailablePlats = "ios" | "ipad" | "iphone" | "android" | "phablet" | "tablet" | "cordova" | "capacitor" | "electron" | "pwa" | "mobile" | "mobileweb" | "desktop" | "hybrid";

/**
 * `platformService` is a service that manages platform detection and configuration.
 * It provides methods and properties to determine the type of device and control the display mode,
 * as well as emitting events related to configuration changes.
 * 
 * @remarks
 * This service is injected at the root level and depends on `DeviceDetectorService`, `Platform`, `Router`, and `ActivatedRoute`.
 */
@Injectable({
    providedIn: 'root'
})
export class platformService {
    private _platforms: Array <AvailablePlats> = [];
    
    /** Indicates whether the current device is a mobile device. */
    public _isMobile = false;

    /** Indicates whether the current device is a desktop device. */
    public _isDesktop = false;
    
    private _onKioskChanged = new Subject<null> ();
    private _onThemeChanged = new Subject<null> ();

    /** Observable that emits when kiosk mode changes. */
    public OnKioskChanged = this._onKioskChanged.asObservable();

    /** Observable that emits when the theme changes. */
    public OnThemeChanged = this._onThemeChanged.asObservable();

    /**
     * Retrieves detailed device information from `DeviceDetectorService`.
     * 
     * @returns An object containing details about the device, such as OS, browser, and device type.
     */    
    get DeviceInfo(){
        return this.detector.getDeviceInfo();
    }

    private _isready = false;
    private _kioskmode = false;
    private _bodytheme: 'dark' | 'light' | null = null;
    private _ostheme: 'dark' | 'light' = 'dark';
    private _viewclass: 'mobile' | 'desktop' | null = null;
    private _bodyzoom = 1;
    private _showscroll = false;

     /**
     * Retrieves the current kiosk mode status.
     * 
     * @returns `true` if the device is in kiosk mode, otherwise `false`.
     */
     get _isKiosk(){
        return (this.LegacyMobile || this.LegacyTablet) ? false : this._kioskmode;
    }

    /**
     * Sets the kiosk mode and emits an event when the mode changes.
     * 
     * @remarks 
     * Kiosk mode can only be enabled if the device is not recognized as a mobile or tablet device.
     * 
     * @param value - A boolean indicating whether kiosk mode should be enabled (`true`) or disabled (`false`).
     */    
    set _isKiosk(value){
        if (this.LegacyMobile || this.LegacyTablet){
            return;     // cannot set kiosk when native keyboard is available
        }

        if (value != this._kioskmode){
            this._kioskmode = value;
            this._onKioskChanged.next(null);

            if (this._kioskmode){
                console.info("[KIOSK] using virtual keyboard");
            }
            else {
                console.info("[KIOSK] using phisical keyboard");
            }    
        }
    }

    /**
     * OS color scheme from `prefers-color-scheme`.
     */
    get _osTheme(): 'dark' | 'light' {
        return this._ostheme;
    }

    /**
     * Effective theme: an explicit override if set, otherwise {@link _osTheme}.
     */
    get _theme(): 'dark' | 'light' | null {
        return this._bodytheme ?? this._ostheme;
    }

    /**
     * Sets an explicit theme override and applies it on `document.body`.
     */
    set _theme(value: 'dark' | 'light'){
        if (value != this._bodytheme){
            this._bodytheme = value;
            this._ApplyBodyTheme(value);
            this._onThemeChanged.next(null);
        }
    }

    private _ApplyBodyTheme(value: 'dark' | 'light'): void {
        if (document.body.classList.contains('dark') && value != 'dark'){
            document.body.classList.remove('dark');
        }
        if (document.body.classList.contains('light') && value != 'light'){
            document.body.classList.remove('light');
        }
        if (!document.body.classList.contains(value)){
            document.body.classList.add(value);
        }
    }

    private _OnOsThemeChange = (_event: MediaQueryListEvent): void => {
        this._ostheme = _event.matches ? 'dark' : 'light';
        if (this._bodytheme == null){
            this._ApplyBodyTheme(this._ostheme);
        }
        this._onThemeChanged.next(null);
    };

    /**
     * Applies the `mobile` / `desktop` class on `document.body`, replacing any previous view mode class.
     * Unlike theme, the application always resolves to exactly one of the two modes: there is no
     * neutral visual representation, so `null` is not an accepted value here.
     * Same application point as theme classes; does not touch `dark` / `light`.
     * 
     * @param value - The view mode class to apply.
     */
    set _bodyview(value: 'mobile' | 'desktop'){
        if (value != this._viewclass){
            if (this._viewclass){
                document.body.classList.remove(this._viewclass);
            }

            this._viewclass = value;
            document.body.classList.add(this._viewclass);
        }
    }

    /**
     * Retrieves the current zoom level of the application.
     * 
     * @returns The current zoom level as a numeric value.
     */    
    get _zoomLevel(){
        return this._bodyzoom;
    }

    /**
     * Sets the current zoom level of the application.
     * 
     * @param value - The new zoom level to apply.
     */    
    set _zoomLevel(value){
        if (this._bodyzoom != value){
            this._bodyzoom = value;

            const _root = document.documentElement;
            if (_root){
                _root.style.setProperty('--body-scale-width', ((1 / this._bodyzoom) * 100).toString() + '%');
                _root.style.setProperty('--body-scale-height', ((1 / this._bodyzoom) * 100).toString() + '%');
                _root.style.setProperty('--body-scale-level', this._bodyzoom.toString());

                _root.style.setProperty('--body-zoom-level', (this._bodyzoom * 100).toString() + '%');
                _root.style.setProperty('--body-zoom-width', '100%');
                _root.style.setProperty('--body-zoom-height', ((1 - this._bodyzoom) * 100) + '%');
            }    
        }
    }

    /**
     * Retrieves the visibility status of the scrollbar.
     * 
     * @returns `true` if the scrollbar is visible, otherwise `false`.
     */    
    get _scrollbar(){
        return this._showscroll;
    }

    /**
     * Toggles the display of the scroll bar based on the provided value.
     * 
     * @param value - A boolean indicating whether the scroll bar should be shown (`true`) or hidden (`false`).
     */    
    set _scrollbar(value){
        this._showscroll = value;
        this._onThemeChanged.next(null);
    }

    /************************/
    /* OS information       */
    /************************/

    private _runningos: OSInfo | null = null;

    private get _OS(): OSInfo | null {
        const _os: OSInfo = {
            syst: null,     // win / linux / mac
            proc: null,     // 32 / 64
            arch: null,     // ARM / intel
            fext: null,     // executable files extension
        }
    
        const _platform = navigator.platform?.toLowerCase();
        const _useragnt = navigator.userAgent.toLowerCase();

        // check with useragnt
        let _win = _useragnt.includes('win');
        let _lin = _useragnt.includes('linux') || _useragnt.includes('x11');
        let _mac = _useragnt.includes('mac');
        let _arm = _useragnt.includes('arm') || _useragnt.includes('aarch') || _useragnt.includes('apple silicon');;
        
        // complete with platform
        _win = _win || _platform.includes('win');
        _lin = _lin || _platform.includes('linux') || _platform.includes('x11');
        _mac = _mac || _platform.includes('mac');

        if (_win){
            const _is64 = _useragnt.includes('win64') || _useragnt.includes('wow64');
            
            _os.proc = _is64 ? '64' : '32';
            _os.arch = _arm ? 'arm': 'intel';
            _os.syst = 'win';
            _os.fext = '.exe';
        }

        if (_lin){
            const _is64 = _useragnt.includes('x86_64') || _useragnt.includes('amd64');

            _os.proc = _is64 ? '64' : '32';
            _os.arch = _arm ? 'arm': 'intel';
            _os.syst = 'linux'
            _os.fext = '';
        }

        if (_mac){
            _arm = _arm || _useragnt.includes('apple') && navigator.maxTouchPoints > 0;

            _os.proc = '64';
            _os.arch = _arm ? 'arm': 'intel';
            _os.syst = 'mac'
            _os.fext = '';
        }

        if (_os.syst && _os.proc && _os.arch){
            return _os;
        }

        return null;
    }
    
    /**
     * Returns the Operating System information object.
     * 
     * @returns An object containing system type, processor type, architecture, and file extension for executables
     */    
    get RunningOS(){  
        if (!this._runningos){
            this._runningos = this._OS;
        }
        return this._runningos;
    }    

    /************************/
    /* Browser information  */
    /************************/

    private _browser: string | null = null;

    private get _Browser(){ // https://www.geeksforgeeks.org/how-to-detect-the-user-browser-safari-chrome-ie-firefox-and-opera-using-javascript/
        // Get the user-agent string
        const userAgentString = navigator.userAgent;
               
        const chromeAgent = userAgentString.includes("Chrome");
        const IExplorerAgent = userAgentString.includes("MSIE") || userAgentString.includes("rv:");
        const firefoxAgent = userAgentString.includes("Firefox");
        const safariAgent = userAgentString.includes("Safari");
        const operaAgent = userAgentString.includes("OP");
        
        // the evaluation order is important!
        if (operaAgent){
            return "Opera";
        }

        if (chromeAgent){
            return "Chrome";
        }

        if (safariAgent){
            return "Safari";
        }

        if (firefoxAgent){
            return "Firefox";
        }

        if (IExplorerAgent){
            return "IE";
        }

        return null;
    }
    
    /**
     * Returns the browser type as a string (e.g., "Chrome", "Safari", "Firefox").
     * 
     * @returns The name of the browser or `null` if it could not be determined.
     */    
    get Browser(){  
        if (!this._browser){
            this._browser = this._Browser;
        }
        return this._browser;
    }

    /****************************/
    /* VIEW MODE                */
    /****************************/
    
    private _forcedmode: string | null = null;
    private ForceMode(mode: string){
        switch (mode.toLowerCase()){
            case 'desktop':
                this._forcedmode = 'desktop';
                break;

            case 'mobile':
                this._forcedmode = 'mobile';
                break;

            case 'auto':
                this._forcedmode = null;
                break;
        }
    }

    set _viewMode(value: string){
        const _mode = value.toLowerCase();
        
        let _reload = false;
        if ((_mode == 'mobile') && !this._ismobile){
            _reload = true;
        }

        if ((_mode == 'desktop') && !this._isdesktop){
            _reload = true;
        }

        if (_mode == 'auto'){
            const _urlMode = new URLSearchParams(window.location.search).get('mode')?.toLowerCase();
            if (this._forcedmode != null || _urlMode === 'mobile' || _urlMode === 'desktop'){
                _reload = true;
            }
        }

        if (_reload){
            this.ForceMode(_mode);
            console.info("[FORCE MODE] forcing to '" + _mode + "' and reloading");

            this.router.navigate([], {
                relativeTo: this.route,
                queryParams: {
                    mode: _mode
                },
    
                queryParamsHandling: 'merge',
                skipLocationChange: false
              }).then(() => {
                    window.location.reload();
              });                
        }
        else if (_mode == 'auto' || _mode == 'mobile' || _mode == 'desktop'){
            this.ForceMode(_mode);
        }
    }

    /****************************/
    /* STARTS HERE              */
    /****************************/

    private _angular_add(_platforms: Array <AvailablePlats>){
        for(const _platform of _platforms) {
            if (this.platform.is(_platform)){
                this._platforms.push(_platform);
            }
        }
    }

    private _detector_add(_platforms: Array <AvailablePlats>){
        for(const _platform of _platforms) {
            let _available = false;

            switch(_platform){
                case 'mobile':
                    _available = this.detector.isMobile();
                    break;
                case 'tablet':
                    _available = this.detector.isTablet();
                    break;
                case 'desktop':
                    _available = this.detector.isDesktop();
                    break;
            }

            if (_available){
                this._platforms.push(_platform);
            }
        }
    }

    private _initialize(){
        this._platforms = []

        console.info("[DEVICE] running on browser: [" + this.Browser + "]")
        console.info("[DEVICE] identified platforms ", this.platform.platforms());

        const _urlMode = this.route.snapshot.queryParamMap.get("mode");
        if (_urlMode){  // use url mode
            this.ForceMode(_urlMode);        
        }

        this._angular_add(['cordova', 'ios', 'android']);
        this._detector_add(['mobile', 'tablet', 'desktop']);
        
        if (this._forcedmode){
            console.info("[DEVICE] platform forced to ", [ this._forcedmode ]);        
        }
        else {
            console.info("[DEVICE] running on platforms ", this._platforms);        
        }

        // retrieve the device mode in the url parameters
        this.route.queryParams.pipe(take(1)).subscribe(
        params => {
            if ('mode' in params){
                this.ForceMode(params['mode']);      

                if (this._forcedmode){
                    console.info("[DEVICE] platform forced to ", [ this._forcedmode ]);        
                }
                else {
                    console.info("[DEVICE] running on platforms ", this._platforms);        
                }
        
                this._isDesktop = this._isdesktop;
                this._isMobile = this._ismobile;
                this._bodyview = (this._isMobile) ? 'mobile' :  'desktop';
            }
        });

        this._isready = true;
    }

    /**
     * Seeds `body.mobile` / `body.desktop` before Ionic `platform.ready()`.
     * Email-result routes render outside the login state machine and must see the
     * mode class on first paint (forced `?mode=` or provisional device detection).
     */
    private _seedBodyViewClass(): void {
        const urlMode = new URLSearchParams(window.location.search).get('mode');
        if (urlMode) {
            this.ForceMode(urlMode);
        }

        if (this._forcedmode === 'mobile' || this._forcedmode === 'desktop') {
            this._bodyview = this._forcedmode;
            return;
        }

        if (this.detector.isMobile() && !this.detector.isTablet()) {
            this._bodyview = 'mobile';
        } else {
            this._bodyview = 'desktop';
        }
    }

    /**
     * Constructor that initializes the platform service with required dependencies and sets up the platform configurations.
     * 
     * @param detector - Device detector service for detecting device-specific information.
     * @param platform - Ionic platform service for managing platform-specific features.
     * @param router - Angular router for navigation.
     * @param route - Activated route for accessing route-specific information.
     */    
    constructor(private detector: DeviceDetectorService, private platform: Platform, private router: Router, private route: ActivatedRoute) {
        const _query = window.matchMedia('(prefers-color-scheme: dark)');
        this._ostheme = _query.matches ? 'dark' : 'light';
        
        _query.addEventListener('change', this._OnOsThemeChange);
        this._ApplyBodyTheme(this._ostheme);

        this._seedBodyViewClass();

        this.platform.ready().then(() => {
            this._initialize();

            this._isMobile = this._ismobile;
            this._isDesktop = this._isdesktop;
            this._bodyview = this._isMobile ? 'mobile' : 'desktop';
        });
    }
    
    /****************************************/
    /* Wrap the platform used methods       */
    /****************************************/

    /**
     * Returns the platform ready status
     * 
     * @returns True when the platform is ready
     */    
    get IsReady(){
        return this._isready;        
    }

    /**
     * Wraps the platform resize event.
     * 
     * @returns An observable that emits whenever the platform resize event occurs.
     */    
    get resize(){
        return this.platform.resize;
    }
    
    /**
     * Resolves when the platform is ready.
     * 
     * @returns A promise that resolves when the platform setup is complete.
     */    
    ready(){
        return this.platform.ready();
    }
        
    /**
     * Returns an array of detected platform types.
     * 
     * @returns An array containing the available platform types.
     */    
    platforms(){
        return this._platforms;
    }
    
    /**
     * Determines whether the current platform matches the specified type.
     * 
     * @param _platform - The platform type to check (e.g., "ios", "android", "desktop").
     * @returns `true` if the specified platform is detected; otherwise, `false`.
     */    
    is(_platform: AvailablePlats) {
        const _retval = (this._platforms.includes(_platform));
        if (!_retval){  
            // specific android check
            if (_platform == 'android'){
                if (window.navigator.userAgent.toLowerCase().includes('android')) {
                    return true;
                }
            }

            // specific ios check
            if (_platform == 'ios'){
                if (/iPad|iPhone|iPod/.test(window.navigator.userAgent)) {
                    return true;
                }
            }
        }

        return _retval; 
    }    
    
    /****************************************/
    /* Query large devices / mobile devices */
    /****************************************/
    
    private get _mobileOS(){
        const _test: Array<'ios' | 'android'> = ['ios', 'android'];

        for (const _os of _test){
            if (this.platform.is(_os) || this.is(_os)){
                return true;
            }    
        }

        return false;
    }

    private get _ismobile(){    // true if is mobile (not tablet)
        if (this._forcedmode){
            return (this._forcedmode == 'mobile');
        }

        return this.LegacyMobile;
    }

    private get _isdesktop(){
        if (this._forcedmode){
            return (this._forcedmode == 'desktop');
        }

        return !this._ismobile
    }

    /**
     * Determines if the current device should be considered as a legacy mobile device.
     * 
     * @returns `true` if the device is a legacy mobile device; otherwise, `false`.
     */    
    get LegacyMobile(){
        if (!this._isready){
            console.error("[PLATFORM] not ready!")
            return false;
        }
 
        if (this.platform.is('tablet')){
            return this.platform.is('iphone');  // iphones may identify themselves as tablet devices
        }
        else {
            return this.platform.is('mobile') && this._mobileOS;
        }
    }

    /**
     * Provisional hardware heuristic for a phone-sized touch device (Pinpad warnings).
     * True when the screen is touch-capable and the shorter edge is at most 820 px.
     * Independent of `view.Mobile` / forced app profiles and of {@link LegacyMobile}.
     */
    get SeemsMobile(): boolean {
        const touchPoints = typeof navigator !== 'undefined' ? Number(navigator.maxTouchPoints || 0) : 0;
        if (!(touchPoints > 0)) {
            return false;
        }

        if (typeof screen === 'undefined') {
            return false;
        }

        const shortEdge = Math.min(Number(screen.width) || 0, Number(screen.height) || 0);
        return shortEdge > 0 && shortEdge <= 820;
    }

    /**
     * Determines if the current device should be considered as a legacy tablet device.
     * 
     * @returns `true` if the device is a legacy tablet device; otherwise, `false`.
     */    
    get LegacyTablet(){
        if (!this._isready){
            console.error("[PLATFORM] not ready!")
            return false;
        }

        return !this.LegacyMobile && !this.platform.is('desktop') && this.platform.is('tablet') && this._mobileOS;
    }

    /**
     * Determines if the current device should be considered as a legacy desktop device.
     * 
     * @returns `true` if the device is a legacy desktop device; otherwise, `false`.
     */    
    get LegacyDesktop(){
        if (!this._isready){
            console.error("[PLATFORM] not ready!")
            return false;
        }

        return !this.LegacyMobile;
    }
}
